import React, { useMemo, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Users, ClipboardList, Timer, ChevronRight, Clock } from "lucide-react";
import { format, addDays, isToday, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
import { useUserStore } from "@/store/userStore";
import { useToast } from "@/hooks/use-toast";
import { getUserProfilesBulk } from "@/services/edgeFunctionWrappers/profilesManagementWrapper";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { CoachHeroSection } from "@/components/coach/dashboard/CoachHeroSection";
import { CoachKpiCard } from "@/components/coach/dashboard/CoachKpiCard";
import { useTranslation } from "react-i18next";

// -----------------------------
// Helpers
// -----------------------------
const toISO = (d: Date) => d.toISOString().split("T")[0];
const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((s) => s[0]?.toUpperCase())
    .slice(0, 2)
    .join("");

type Assignment = {
  bookingId: string;
  classId: string;
  className: string;
  classDate: string;
  classTime: string | null;
  duration: string | number | null;
  userId: string;
  userName: string;
  userPhoto: string | null;
};

// -----------------------------
// Data loader (RLS-friendly) — UNCHANGED LOGIC
// -----------------------------
async function fetchCoachDashboardData(coachId: string, day: Date) {
  const dateISO = toISO(day);

  const { data: bookings, error: bErr } = await supabase
    .from("class_bookings")
    .select("id, class_id, class_date, user_id, status, time, duration")
    .eq("class_date", dateISO)
    .not("status", "eq", "cancelled");
  if (bErr) throw bErr;

  const allBookingIds = (bookings ?? []).map((b) => b.id);
  const allClassIds = Array.from(new Set((bookings ?? []).map((b) => b.class_id)));

  const { data: myCbc, error: aErr } = await supabase
    .from("class_booking_coach")
    .select("id, class_booking_id, coach_id, created_at")
    .in("class_booking_id", allBookingIds);
  if (aErr) throw aErr;

  const { data: classes, error: cErr } = await supabase
    .from("scheduled_classes")
    .select("id, name, class_date, time, duration, capacity, gym_id")
    .in("id", allClassIds)
    .order("time", { ascending: true });
  if (cErr) throw cErr;

  const mineBookingIds = (myCbc ?? []).map((m) => m.class_booking_id);
  const mineBookings = (bookings ?? []).filter((b) => mineBookingIds.includes(b.id));
  const userIds = Array.from(new Set(mineBookings.map((b) => b.user_id)));
  const { data: users, error: uErr } = await getUserProfilesBulk(userIds);
  if (uErr) throw uErr;

  const assignments: Assignment[] = mineBookings.map((b) => {
    const klass = classes?.find((k) => k.id === b.class_id);
    const up = users?.find((u) => u.id === b.user_id);
    return {
      bookingId: b.id,
      classId: b.class_id,
      className: klass?.name ?? "",
      classDate: b.class_date,
      classTime: b.time ?? null,
      duration: b.duration ?? klass?.duration ?? "45",
      userId: b.user_id,
      userName: `${up?.first_name ?? ""} ${up?.last_name ?? ""}`.trim(),
      userPhoto: up?.profile_photo_url ?? null,
    };
  });

  const bookedByClass: Record<string, number> = {};
  (bookings ?? []).forEach((b) => {
    bookedByClass[b.class_id] = (bookedByClass[b.class_id] ?? 0) + 1;
  });

  const distinctSessions = new Set(assignments.map((a) => a.classId)).size;
  const totalAssigned = assignments.length;
  const totalBookingsAll = bookings?.length ?? 0;

  const last7 = Array.from({ length: 7 }).map((_, i) => {
    const d = addDays(day, -6 + i);
    const di = toISO(d);
    const count = (myCbc ?? [])
      .map((m) => bookings?.find((b) => b.id === m.class_booking_id))
      .filter((b) => b?.class_date === di).length;
    return { date: di, value: count };
  });

  return {
    assignments,
    classes: classes ?? [],
    bookedByClass,
    distinctSessions,
    totalAssigned,
    totalBookingsAll,
    spark: last7,
  };
}

// -----------------------------
// Assignment row (glass)
// -----------------------------
function AssignmentRow({ a, index }: { a: Assignment; index: number }) {
  const { t } = useTranslation("coaches");
  const when = a.classTime ? `${a.classTime} · ${a.duration}m` : `${a.duration}m`;
  return (
    <div
      className="glass-stat-card p-3 flex items-center justify-between gap-3"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Avatar className="h-8 w-8 shrink-0">
          {a.userPhoto ? (
            <AvatarImage src={a.userPhoto} />
          ) : (
            <AvatarFallback>{getInitials(a.userName || "U")}</AvatarFallback>
          )}
        </Avatar>
        <div className="text-xs min-w-0">
          <div className="font-medium truncate">{a.userName || t("dashboard.member", { defaultValue: "Adhérent" })}</div>
          <div className="text-muted-foreground truncate">{a.className}</div>
        </div>
      </div>
      <Badge variant="secondary" className="rounded-full shrink-0">
        {when}
      </Badge>
    </div>
  );
}

// -----------------------------
// Main dashboard
// -----------------------------
export default function CoachDashboard() {
  const { t } = useTranslation("coaches");
  const { user, role } = useUserStore();
  const { toast } = useToast();
  const coachId = user?.id;
  const isCoach = role === "coach" || role === "admin";

  const [date] = useState<Date>(new Date());
  const locale = fr;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["coach-pro", coachId, toISO(date)],
    enabled: !!coachId && isCoach,
    queryFn: () => {
      if (!coachId) throw new Error("Coach session unavailable");
      return fetchCoachDashboardData(coachId, date);
    },
  });

  const dayLabel = useMemo(() => {
    const base = format(date, "EEEE d MMMM yyyy", { locale });
    return isToday(date) ? `${base} · ${t("dashboard.today", { defaultValue: "Aujourd'hui" })}` : base;
  }, [date, locale]);

  useEffect(() => {
    if (isError) {
      toast({
        title: t("dashboard.loadError", { defaultValue: "Erreur" }),
        description: t("dashboard.loadErrorDescription", { defaultValue: "Impossible de charger le dashboard." }),
        variant: "destructive",
      });
    }
  }, [isError, toast]);

  if (!isCoach) {
    return (
      <div className="athlete-page-bg min-h-screen p-4">
        <div className="rounded-2xl border p-6 text-center text-sm text-muted-foreground">
          {t("dashboard.accessCoachOnly", { defaultValue: "Accès réservé aux coachs." })}
        </div>
      </div>
    );
  }

  // Rappels: prochaines 2h
  const now = new Date();
  const soon = (data?.assignments ?? [])
    .filter((a) => {
      if (!a.classTime) return false;
      const dt = new Date(`${a.classDate}T${a.classTime}:00`);
      return dt >= now && dt <= new Date(now.getTime() + 2 * 60 * 60 * 1000);
    })
    .sort((a, b) => (a.classTime || "").localeCompare(b.classTime || ""));

  const sessions = (data?.classes ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    time: c.time,
    duration: c.duration ?? "45",
    capacity: c.capacity ?? 0,
    booked: data?.bookedByClass?.[c.id] ?? 0,
  }));

  const sparkDiff =
    (data?.spark?.at(-1)?.value ?? 0) - (data?.spark?.at(-2)?.value ?? 0);

  return (
    <div className="coach-page-bg min-h-screen flex flex-col w-full">
      <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 md:p-6">
        <div className="animate-fade-in space-y-4 pb-20 md:space-y-5 lg:pb-4">
          {/* Hero */}
          <CoachHeroSection
            todaySessionsCount={data?.distinctSessions ?? 0}
            dayLabel={dayLabel}
          />

          {/* KPIs */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {isLoading ? (
              <>
                <Skeleton className="h-36 rounded-md md:h-40" />
                <Skeleton className="h-36 rounded-md md:h-40" />
                <Skeleton className="h-36 rounded-md md:h-40" />
              </>
            ) : (
              <>
                <CoachKpiCard
                  icon={Calendar}
                  label={t("dashboard.sessionsToCoach", { defaultValue: "Séances à coacher" })}
                  value={data?.distinctSessions ?? 0}
                  trend={{ diff: sparkDiff }}
                  data={data?.spark}
                  index={0}
                  compact
                />
                <CoachKpiCard
                  icon={Users}
                  label={t("dashboard.membersAssigned", { defaultValue: "Adhérents assignés" })}
                  value={data?.totalAssigned ?? 0}
                  trend={{ diff: sparkDiff }}
                  data={data?.spark}
                  index={1}
                  compact
                />
                <CoachKpiCard
                  icon={ClipboardList}
                  label={t("dashboard.bookingsToday", { defaultValue: "Réservations (jour)" })}
                  value={data?.totalBookingsAll ?? 0}
                  index={2}
                  compact
                />
              </>
            )}
          </div>

          {/* Rappels prochaines 2h */}
          <section className="coach-reminder-card p-3 md:p-4">
            <div className="flex items-center gap-3">
              <span className="coach-icon-box flex h-11 w-11 shrink-0 items-center justify-center rounded-md"><Timer className="h-5 w-5 text-primary" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{t("dashboard.reminders", { defaultValue: "Rappels (prochaines 2h)" })}</div>
                {soon.length === 0 && !isLoading && <p className="mt-0.5 text-xs text-muted-foreground">{t("dashboard.noReminder", { defaultValue: "Aucun rappel imminent." })}</p>}
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
            {isLoading ? (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Skeleton className="h-16 rounded-xl" />
                <Skeleton className="h-16 rounded-xl" />
              </div>
            ) : soon.length > 0 ? (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {soon.map((a, i) => (
                  <AssignmentRow key={a.bookingId} a={a} index={i} />
                ))}
              </div>
            ) : null}
          </section>

          {/* Séances du jour */}
          <div className="space-y-2">
            <div className="flex items-center justify-between px-1"><h2 className="text-xl font-bold">{t("dashboard.todaySessions", { defaultValue: "Séances du jour" })}</h2><span className="text-xs text-muted-foreground">{sessions.length} {t("dashboard.sessionCount", { defaultValue: "séances" })}</span></div>
            {isLoading ? (
              <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                <Skeleton className="h-20 rounded-xl" />
                <Skeleton className="h-20 rounded-xl" />
                <Skeleton className="h-20 rounded-xl" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="glass-stat-card p-4 text-xs text-muted-foreground text-center">
                {t("dashboard.noSession", { defaultValue: "Aucune séance planifiée." })}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sessions.map((s, i) => (
                  <div
                    key={s.id}
                    className="coach-session-card relative flex min-h-16 items-center gap-3 overflow-hidden px-3 py-2 pl-5"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <span className="absolute inset-y-2 left-0 w-1 rounded-full bg-primary" />
                    <strong className="w-16 shrink-0 text-lg tabular-nums">{s.time ?? "—"}</strong>
                    <div className="min-w-0 flex-1 border-l border-border pl-3 text-xs">
                      <div className="truncate text-sm font-bold">{s.name}</div>
                      <div className="flex items-center gap-1 text-muted-foreground"><Clock className="h-3 w-3" />{s.duration} min</div>
                    </div>
                    <Badge variant="secondary" className="shrink-0 rounded-full px-2 py-1 tabular-nums">
                      <Users className="mr-1 h-3.5 w-3.5 text-primary" />
                      {s.booked}/{s.capacity}
                    </Badge>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
